/**
 * Triple detection and golden unit creation for auto-battler mechanics
 */

import { Card } from './data/cards';

/**
 * Finds all triples (3-of-a-kind) on a board/hand based on base card ID
 * @param cards - Array of card instances to check
 * @returns Array of triples, where each triple is an array of 3 indices
 */
export function findTripleIndices(cards: (Card | null)[]): number[][] {
  const triples: number[][] = [];

  // Group cards by their base ID
  const cardGroups = new Map<string, number[]>();

  cards.forEach((card, index) => {
    if (!card) return;

    const baseId = card.id;
    if (!cardGroups.has(baseId)) {
      cardGroups.set(baseId, []);
    }
    cardGroups.get(baseId)!.push(index);
  });

  // Find groups of 3 or more
  for (const [cardId, indices] of cardGroups.entries()) {
    // Process all complete triples (groups of 3)
    while (indices.length >= 3) {
      const triple = indices.splice(0, 3);
      triples.push(triple);
    }
  }

  return triples;
}

/**
 * Creates a golden (upgraded) card from three normal cards
 * @param triple - Array of 3 card instances to merge
 * @param baseCard - The base card template
 * @returns A new golden card instance
 */
export function createGoldenFromTriple(triple: Card[], baseCard: Card): Card {
  if (triple.length !== 3) {
    throw new Error('Triple must contain exactly 3 cards');
  }

  // Golden units have doubled base stats
  const goldenCard: Card = {
    ...baseCard,
    attack: baseCard.attack * 2,
    health: baseCard.health * 2,
    instanceId: crypto.randomUUID(),
    isGolden: true,
    name: `${baseCard.name} ★`, // Add star to indicate golden
  };

  return goldenCard;
}

/**
 * Checks if a card is golden
 * @param card - Card to check
 * @returns true if the card is golden
 */
export function isGolden(card: Card): boolean {
  return card.isGolden === true;
}

/**
 * Gets the tier of card that should be offered as a triple reward
 * @param baseTier - The tier of the cards that were tripled
 * @returns The tier to offer (capped at max tier)
 */
export function getTripleRewardTier(baseTier: number): number {
  // Offer a card one tier higher, but cap at tier 6
  return Math.min(baseTier + 1, 6);
}
